import React, { useEffect, useMemo, useRef, useState } from "react";
import { Search, ChevronDown, Check } from "lucide-react";
import { inputCls, TextInput } from "./formPrimitives";

export const OTHER_ID = "__other__";

export const otherLabel = (categoryName = "") => {
  const n = categoryName.toLowerCase();
  if (n.includes("iphone")) return "Annen iPhone-modell";
  if (n.includes("samsung")) return "Annen Samsung-modell";
  if (n.includes("ipad")) return "Annen iPad-modell";
  if (n.includes("macbook")) return "Annen MacBook-modell";
  return "Annen modell";
};

export const otherHint = (categoryName = "") => {
  const n = categoryName.toLowerCase();
  if (n.includes("ipad")) return "Skriv inn modell eller A-nummer";
  if (n.includes("macbook")) return "Skriv inn modell, år eller A-nummer";
  return "Skriv inn nøyaktig modell";
};

const norm = (s = "") => s.toLowerCase().replace(/[\u2033\u201d"]/g, '"').replace(/\s+/g, " ");

export const filterModels = (models, query) => {
  const q = norm(query).trim();
  if (!q) return models;
  const terms = q.split(" ");
  return models.filter((m) => { const n = norm(m.name); return terms.every((t) => n.includes(t)); });
};

export const groupModels = (models) => {
  const out = [];
  models.forEach((m) => {
    const g = m.group || "";
    const last = out[out.length - 1];
    if (last && last.group === g) last.items.push(m); else out.push({ group: g, items: [m] });
  });
  return out;
};

/* Searchable, grouped model picker. value: model object | { id: OTHER_ID } | null */
export default function ModelPicker({ models, value, onChange, categoryName, otherText, onOtherText, otherError, error, testid = "model-picker", searchPlaceholder = "Søk etter modell ..." }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  const other = { id: OTHER_ID, name: otherLabel(categoryName) };

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => filterModels(models, query), [models, query]);
  const groups = useMemo(() => groupModels(filtered), [filtered]);
  const pick = (m) => { onChange(m); setOpen(false); setQuery(""); };
  const isOther = value?.id === OTHER_ID;

  return (
    <div ref={ref} className="relative" data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className={`${inputCls} flex items-center justify-between gap-2 text-left ${error ? "border-red-400" : ""} ${!value ? "text-ink/40" : ""}`}
        data-testid={`${testid}-toggle`}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{value ? value.name : "Velg modell"}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-ink/40 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1.5 w-full rounded-xl border border-hairline bg-white shadow-lg" data-testid={`${testid}-dropdown`}>
          <div className="relative border-b border-hairline p-2">
            <Search className="pointer-events-none absolute left-5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={searchPlaceholder}
              className={`${inputCls} pl-9 py-2`}
              data-testid={`${testid}-search`}
            />
          </div>
          <ul role="listbox" className="max-h-64 overflow-y-auto overscroll-contain py-1">
            {groups.map(({ group, items }) => (
              <li key={group || "_"}>
                {group && <div className="px-3.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink/45">{group}</div>}
                <ul>
                  {items.map((m) => (
                    <li key={m.id}>
                      <button type="button" role="option" aria-selected={value?.id === m.id} onClick={() => pick(m)}
                        className={`flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-[15px] hover:bg-electric/5 ${value?.id === m.id ? "text-electric font-medium" : "text-navy"}`}
                        data-testid={`${testid}-option-${m.name}`}>
                        <span>{m.name}</span>
                        {value?.id === m.id && <Check className="h-4 w-4 shrink-0" />}
                      </button>
                    </li>
                  ))}
                </ul>
              </li>
            ))}
            {filtered.length === 0 && <li className="px-3.5 py-2 text-sm text-ink/50" data-testid={`${testid}-empty`}>Ingen treff – velg «{other.name}» under.</li>}
            <li className="mt-1 border-t border-hairline pt-1">
              <button type="button" role="option" aria-selected={isOther} onClick={() => pick(other)}
                className={`flex w-full items-center justify-between gap-2 px-3.5 py-2 text-left text-[15px] font-medium hover:bg-electric/5 ${isOther ? "text-electric" : "text-navy"}`}
                data-testid={`${testid}-option-other`}>
                <span>{other.name}</span>
                {isOther && <Check className="h-4 w-4 shrink-0" />}
              </button>
            </li>
          </ul>
        </div>
      )}

      {isOther && (
        <div className="mt-2">
          <TextInput value={otherText} onChange={(e) => onOtherText(e.target.value)} placeholder={otherHint(categoryName)} error={otherError} data-testid={`${testid}-other-input`} />
          {otherError && <span className="mt-1 block text-xs font-medium text-red-600">{otherError}</span>}
        </div>
      )}
    </div>
  );
}
